import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PrinterPromotion = () => {
    // --- State ---
    const [prints, setPrints] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // NEW: Track whether we are adding a new print or changing an existing one
    const [uploadMode, setUploadMode] = useState('ADD');

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Printer Promotion",
            subtitle: "View and share printable promotional materials",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Print Materials...",
            noData: "No print materials found.",
            btnChange: "Change Print Photo",
            btnDelete: "Delete Print Photo",
            btnAdd: "Add Print Photo",
            compressing: "Compressing..."
        },
        hi: {
            pageTitle: "प्रिंटर प्रचार",
            subtitle: "प्रिंट करने योग्य प्रचार सामग्री देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "प्रिंट सामग्री लोड हो रही है...",
            noData: "कोई प्रिंट सामग्री नहीं मिली।",
            btnChange: "प्रिंट फोटो बदलें",
            btnDelete: "प्रिंट फोटो हटाएं",
            btnAdd: "प्रिंट फोटो जोड़ें",
            compressing: "संपीड़ित कर रहा है..."
        },
        gu: {
            pageTitle: "પ્રિન્ટર પ્રમોશન",
            subtitle: "છાપવા યોગ્ય પ્રમોશનલ સામગ્રી જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "પ્રિન્ટ સામગ્રી લોડ થઈ રહી છે...",
            noData: "કોઈ પ્રિન્ટ સામગ્રી મળી નથી.",
            btnChange: "પ્રિન્ટ ફોટો બદલો",
            btnDelete: "પ્રિન્ટ ફોટો કાઢી નાખો",
            btnAdd: "પ્રિન્ટ ફોટો ઉમેરો",
            compressing: "સંકુચિત કરી રહ્યા છીએ..."
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchPrints = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "print" 
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setPrints(response.data.data.result);
            } else {
                setPrints([]);
            }
        } catch (error) {
            console.error("Error fetching print promotions:", error);
            setPrints([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPrints();
    }, [appId]);

    // --- Global Page Actions ---
    const handleAddImage = () => {
        setUploadMode('ADD');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleChangeImage = () => {
        setUploadMode('CHANGE');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // --- NEW: Image Compression Logic ---
    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    
                    // Set maximum dimensions
                    const MAX_WIDTH = 1200;
                    const MAX_HEIGHT = 1200;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG with 0.7 quality
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    
                    // Return the raw base64 string
                    resolve(dataUrl.split(',')[1]);
                };
            };
        });
    };

    // NEW: Handle File Upload for both Add and Change actions with Compression
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);

        try {
            // Compress the image to avoid 413 errors
            const compressedBase64 = await compressImage(file);

            const payload = {
                app_id: appId?.toString(),
                prachar_type: "PRINT", // Correct type for Printer Promotion
                base64: compressedBase64,
                content: file.name,
                create_by: userId?.toString() || "1"
            };

            // If changing an existing image, append its ID so the backend updates it
            if (uploadMode === 'CHANGE' && prints.length > 0) {
                payload.id = prints[0].id.toString();
            }

            console.log(`Sending ${uploadMode} Payload:`, { ...payload, base64: "HIDDEN_BASE64" });

            const response = await axios.post(
                `${BASE_URL}/api/ins_prachar_master`,
                payload
            );

            if (response.data.success) {
                await fetchPrints();
            } else {
                alert("Failed to save the print photo. Server responded but success was false.");
            }
        } catch (error) {
            console.error("Error saving image:", error.response ? error.response.data : error.message);
            if (error.response && error.response.status === 413) {
                alert("Error: The image is still too large. Please select a smaller file.");
            } else {
                alert(`An error occurred while saving the image: ${error.message}`);
            }
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // UPDATED: Delete API Call
    const handleDeleteImage = async () => {
        if (prints.length === 0) return;

        if (window.confirm("Are you sure you want to delete this print photo?")) {
            try {
                const photoId = prints[0].id; 

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    {
                        app_id: appId?.toString(),
                        id: photoId?.toString(),
                        delete_by: userId?.toString() || "1"
                    }
                );
                
                if (response.data.success) {
                    fetchPrints();
                } else {
                    alert("Failed to delete the image.");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                alert("An error occurred while deleting the image.");
            }
        }
    };

    // --- Loading State ---
    if (loading && prints.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Buttons) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">    
                    {/* Hidden file input for uploading images */}
                    <input 
                        type="file" 
                        accept="image/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />

                    {/* CONDITIONAL RENDERING FOR BUTTONS - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        prints.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleAddImage}
                                disabled={loading}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {loading ? txt.compressing : txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleChangeImage}
                                    disabled={loading}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {loading ? txt.compressing : txt.btnChange}
                                </button>

                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={handleDeleteImage}
                                    disabled={loading}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {prints.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🖨️</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- PRINT GRID --- */}
            <div className="print-grid pb-5">
                {prints.map((print) => (
                    <div className="print-card" key={print.id}>
                        {/* Image Container */}
                        <div className="image-wrapper">
                            <img 
                                src={print.content} 
                                alt={print.name} 
                                className="promo-image"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = "https://via.placeholder.com/400x300?text=Print+Not+Found";
                                }}
                            />
                        </div>

                        {/* Uploader Details Container (No Buttons Here) */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{print.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {print.designation || "User"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                
                .global-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global (Solid Orange Theme) */
                .btn-add {
                    background: #f97316;
                    color: #ffffff;
                    border: 1px solid #ea580c;
                    box-shadow: 0 4px 6px -1px rgba(249, 115, 22, 0.2);
                }
                .btn-add:hover:not(:disabled) {
                    background: #ea580c;
                    border-color: #c2410c;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(249, 115, 22, 0.3);
                }
                .btn-add:active:not(:disabled) {
                    background: #c2410c;
                    transform: translateY(0);
                }

                /* Edit Button Global */
                .btn-edit {
                    background: #ffffff;
                    color: #334155;
                    border: 1px solid #cbd5e1;
                }
                .btn-edit:hover:not(:disabled) {
                    background: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }
                .btn-edit:active:not(:disabled) {
                    background: #f1f5f9;
                }

                /* Delete Button Global */
                .btn-delete {
                    background: #fef2f2;
                    color: #ef4444;
                    border: 1px solid #fecaca;
                }
                .btn-delete:hover:not(:disabled) {
                    background: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
                .btn-delete:active:not(:disabled) {
                    background: #fecaca;
                }

                /* --- PRINT GRID --- */
                .print-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .print-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                }

                .print-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Image Styling */
                .image-wrapper {
                    width: 100%;
                    height: 220px;
                    background: #f8fafc;
                    overflow: hidden;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .promo-image {
                    width: 100%;
                    height: 100%;
                    object-fit: contain; 
                    transition: transform 0.5s ease;
                }

                .print-card:hover .promo-image {
                    transform: scale(1.03);
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                    border-top: 1px solid #f1f5f9;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge - Orange Theme */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #f97316; 
                    background: #fff7ed;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #fed7aa;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .print-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                    .image-wrapper {
                        height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PrinterPromotion;